<template>
  <div class="report-page">
    <el-page-header
      @back="$router.push('/select')"
      :content="isHistorical ? '历史评估报告' : 'AI骨干人才保留建议报告'"
      class="page-back-header"
    />

    <!-- 历史记录标签 -->
    <div v-if="isHistorical" class="history-badge">
      历史记录 · {{ reportTime }}
    </div>

    <!-- Hero 综合信息卡 -->
    <div class="hero-card animate-fade-in-up">
      <div class="hero-left">
        <div class="hero-avatar">
          {{ employee.real_name ? employee.real_name[0] : '?' }}
        </div>
        <div class="hero-info">
          <h2 class="hero-name">{{ employee.real_name }}</h2>
          <div class="hero-meta">
            {{ employee.dept_name || '-' }} · {{ employee.job_title || '-' }}
          </div>
          <div class="hero-time">
            生成时间: {{ reportTime }}
          </div>
        </div>
      </div>
      <div class="hero-gauge">
        <div class="gauge-ring">
          <svg width="100" height="100" viewBox="0 0 100 100">
            <circle class="gauge-bg" cx="50" cy="50" r="45"/>
            <circle
              class="gauge-fill"
              cx="50" cy="50" r="45"
              :stroke="gaugeColor"
              :stroke-dashoffset="gaugeOffset"
            />
          </svg>
          <div class="gauge-text">
            <div class="gauge-num">{{ report.total_score }}</div>
            <div class="gauge-label">风险分</div>
          </div>
        </div>
        <div class="gauge-risk">
          <div class="risk-tag" :class="'risk-' + report.risk_level">{{ riskLevelText }}</div>
          <div class="risk-hint">{{ riskHint }}</div>
        </div>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div class="report-actions animate-fade-in-up" style="animation-delay: 0.1s">
      <el-button class="btn-action" @click="printReport">打印 / 另存为PDF</el-button>
      <el-button class="btn-action" @click="shareReport">分享报告</el-button>
    </div>

    <!-- 历史评估详情：已勾选行为列表 -->
    <div
      v-if="modulesConfig.behavior_detail && isHistorical && answerDetails.length"
      id="section-answers"
      class="report-card animate-fade-in-up"
      style="animation-delay: 0.15s"
    >
      <div class="card-header">
        <h3 class="card-title">本次评估勾选详情</h3>
        <span class="section-count">{{ answeredYesCount }} 项 / 共 {{ answerDetails.length }} 项</span>
      </div>
      <div class="gold-line"></div>
      <div class="card-body">
        <div class="behavior-answers">
          <div
            v-for="(item, i) in answerDetails"
            :key="i"
            class="behavior-answer-item"
            :class="'answer-' + item.answer"
          >
            <span class="answer-number">{{ i + 1 }}</span>
            <span class="answer-desc">{{ item.category_name }}：{{ item.behavior_desc }}</span>
            <el-tag size="mini" :type="item.answer === 1 ? 'danger' : (item.answer === 2 ? 'success' : 'info')">
              {{ item.answer === 1 ? '是 +' + item.score + '分' : (item.answer === 2 ? '否' : '不了解') }}
            </el-tag>
          </div>
        </div>
      </div>
    </div>

    <!-- 模块1: 风险归因分析 -->
    <div v-if="modulesConfig.risk_analysis" id="section-risk" class="report-card animate-fade-in-up" style="animation-delay: 0.2s">
      <div class="card-header">
        <h3 class="card-title">风险归因分析</h3>
      </div>
      <div class="gold-line"></div>
      <div class="card-body">
        <div class="risk-summary">
          {{ report.risk_analysis || defaultReport.risk_analysis }}
        </div>

        <div class="risk-bars">
          <div v-for="(bar, i) in riskBars" :key="i" class="risk-bar-item">
            <div class="bar-label">{{ bar.label }}</div>
            <div class="bar-track">
              <div class="bar-fill" :style="{ width: bar.value + '%', background: bar.color }"></div>
            </div>
            <div class="bar-val">{{ bar.value }}%</div>
          </div>
        </div>

        <div class="risk-reasons">
          <div v-for="(reason, i) in riskReasons" :key="i" class="risk-reason">
            <span class="reason-number">{{ i + 1 }}</span>
            <span class="reason-text" v-html="formatReason(reason)"></span>
          </div>
        </div>
      </div>
    </div>

    <!-- 模块2: 下一步管理建议 -->
    <div v-if="modulesConfig.suggestions" id="section-suggestion" class="report-card animate-fade-in-up" style="animation-delay: 0.3s">
      <div class="card-header">
        <h3 class="card-title">下一步管理建议</h3>
      </div>
      <div class="gold-line"></div>
      <div class="card-body">
        <div v-if="matchedSuggestions.length" class="suggestion-cards">
          <div
            v-for="(item, i) in matchedSuggestions"
            :key="i"
            class="suggestion-card"
          >
            <div class="suggestion-body">
              <div class="suggestion-title">{{ item.title }}</div>
              <div class="suggestion-content">{{ item.content }}</div>
              <div class="suggestion-action">查看详细方案 &rarr;</div>
            </div>
          </div>
        </div>
        <el-empty v-else description="暂无匹配的管理建议" :image-size="60" />
      </div>
    </div>

    <!-- 模块3: 过往公司内部成功案例 -->
    <div v-if="modulesConfig.cases" id="section-case" class="report-card animate-fade-in-up" style="animation-delay: 0.4s">
      <div class="card-header">
        <h3 class="card-title">过往公司内部成功案例</h3>
      </div>
      <div class="gold-line"></div>
      <div class="card-body">
        <div v-if="matchedCases.length">
          <div v-for="(c, i) in matchedCases" :key="i" class="case-block">
            <div class="case-title">{{ c.title }}</div>
            <div class="case-text">{{ c.content }}</div>
          </div>
        </div>
        <el-empty v-else description="案例库持续建设中" :image-size="60" />
      </div>
    </div>

    <!-- 模块4: 推荐管理课程 / 资源 -->
    <div v-if="modulesConfig.courses" id="section-course" class="report-card animate-fade-in-up" style="animation-delay: 0.5s">
      <div class="card-header">
        <h3 class="card-title">推荐管理课程 / 资源</h3>
      </div>
      <div class="gold-line"></div>
      <div class="card-body">
        <div v-if="matchedCourses.length" class="course-list">
          <div
            v-for="(course, i) in matchedCourses"
            :key="i"
            class="course-row"
          >
            <span class="course-index">{{ String(i + 1).padStart(2, '0') }}</span>
            <div class="course-info">
              <div class="course-title">{{ course.title }}</div>
              <div class="course-desc">{{ course.content }}</div>
            </div>
            <span class="course-arrow">&rarr;</span>
          </div>
        </div>
        <el-empty v-else description="暂无推荐课程" :image-size="60" />
      </div>
    </div>

    <!-- 模块5: 话术与 FAQ -->
    <div v-if="modulesConfig.talking_points" id="section-faq" class="report-card animate-fade-in-up" style="animation-delay: 0.6s">
      <div class="card-header">
        <h3 class="card-title">话术与 FAQ</h3>
      </div>
      <div class="gold-line"></div>
      <div class="card-body">
        <div v-if="matchedFaqs.length" class="faq-list">
          <div v-for="(item, i) in matchedFaqs" :key="i" class="faq-item">
            <div class="faq-question">{{ item.title }}</div>
            <div class="faq-answer">{{ item.content }}</div>
          </div>
        </div>
        <el-empty v-else description="暂无话术与FAQ" :image-size="60" />
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'EvaluationReport',
  computed: {
    employee() {
      const id = parseInt(this.$route.params.id)
      const emp = this.$store.getters.employeeById(id)
      return emp || {}
    },
    isHistorical() {
      return !!this.$route.query.evaluation_id
    },
    historicalEval() {
      const evalId = parseInt(this.$route.query.evaluation_id)
      if (!evalId) return null
      const list = this.$store.state.evaluations || []
      return list.find(e => e.evaluation_id === evalId) || null
    },
    report() {
      if (this.historicalEval) {
        return {
          total_score: this.historicalEval.total_score,
          risk_level: this.historicalEval.risk_level,
          risk_analysis: this.historicalEval.risk_analysis || this.defaultReport.risk_analysis
        }
      }
      const current = this.$store.state.currentEvaluation
      if (current && current.employee_id === this.employee.employee_id) {
        return {
          total_score: current.total_score,
          risk_level: current.risk_level,
          risk_analysis: current.risk_analysis || this.defaultReport.risk_analysis
        }
      }
      return this.defaultReport
    },
    riskLevelText() {
      const level = this.report.risk_level
      return level === 3 ? '高风险' : (level === 2 ? '中风险' : '低风险')
    },
    riskHint() {
      const level = this.report.risk_level
      return level === 3 ? '建议立即干预' : (level === 2 ? '建议近期关注' : '风险可控')
    },
    reportTime() {
      if (this.historicalEval && this.historicalEval.created_at) {
        return this.historicalEval.created_at
      }
      return new Date().toLocaleString('zh-CN')
    },
    answerDetails() {
      if (this.historicalEval && this.historicalEval.answer_details) {
        return this.historicalEval.answer_details
      }
      return []
    },
    answeredYesCount() {
      return this.answerDetails.filter(a => a.answer === 1).length
    },
    employeeBehaviorKeywords() {
      const details = this.answerDetails
      if (!details.length) return []
      const yesBehaviors = details.filter(d => d.answer === 1)
      return yesBehaviors.map(d => (d.behavior_desc || '') + ' ' + (d.category_name || '')).filter(Boolean)
    },
    matchedSuggestions() {
      return this.$store.getters.contentsByRiskAndKeywords(1, this.report.risk_level, this.employeeBehaviorKeywords)
    },
    matchedCases() {
      return this.$store.getters.contentsByRiskAndKeywords(2, this.report.risk_level, this.employeeBehaviorKeywords)
    },
    matchedCourses() {
      return this.$store.getters.contentsByRiskAndKeywords(3, this.report.risk_level, this.employeeBehaviorKeywords)
    },
    matchedFaqs() {
      return this.$store.getters.contentsByRiskAndKeywords(4, this.report.risk_level, this.employeeBehaviorKeywords)
    },
    gaugeColor() {
      const level = this.report.risk_level
      if (level === 3) return '#ef4444'
      if (level === 2) return '#f59e0b'
      return '#22c55e'
    },
    gaugeOffset() {
      const circumference = 2 * Math.PI * 45
      const score = Math.min(Math.max(this.report.total_score || 0, 0), 100)
      return circumference * (1 - score / 100)
    },
  },
  data() {
    return {
      modulesConfig: {
        risk_analysis: true,
        behavior_detail: true,
        suggestions: true,
        cases: true,
        courses: true,
        talking_points: true
      },
      defaultReport: {
        total_score: 25,
        risk_level: 3,
        risk_analysis: '该员工的离职风险主要源于"发展受限"（权重40%）和"外部机会"（权重35%），而非薪酬因素（5%）。'
      },
      riskReasons: [
        '工作压力过大：由于近期接手了新项目，工作量显著增加，且缺乏足够的资源支持',
        '发展空间受限：连续两年未获得晋升，且未收到明确的职业发展路径规划',
        '外部机会吸引：已通过猎头接触外部公司，获得更具吸引力的offer'
      ],
      riskBars: [
        { label: '发展受限', value: 40, color: '#6b7280' },
        { label: '外部机会', value: 35, color: '#6b7280' },
        { label: '工作压力', value: 20, color: '#9ca3af' },
        { label: '薪酬因素', value: 5, color: '#d1d5db' }
      ]
    }
  },
  created() {
    this.loadModulesConfig()
  },
  methods: {
    async loadModulesConfig() {
      try {
        const taskId = this.$store.getters.currentTaskId
        const res = await this.$api.fetchAiReportModules(taskId ? { task_id: taskId } : {})
        if (res.data) {
          const data = res.data
          Object.keys(this.modulesConfig).forEach(key => {
            if (data[key] !== undefined) {
              this.modulesConfig[key] = !!data[key]
            }
          })
        }
      } catch (e) {
        console.warn('[EvaluationReport] 加载模块配置失败:', e.message)
      }
    },
    printReport() {
      window.print()
    },
    shareReport() {
      const url = window.location.href
      const input = document.createElement('input')
      input.value = url
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      this.$message.success('报告链接已复制到剪贴板')
    },
    formatReason(text) {
      const idx = text.indexOf('：')
      if (idx > 0) {
        return '<strong>' + text.slice(0, idx + 1) + '</strong>' + text.slice(idx + 1)
      }
      return text
    }
  }
}
</script>

<style scoped>
/* ===== 页面基础 ===== */
.report-page {
  max-width: 720px;
  margin: 0 auto;
  padding: 24px 16px 48px;
  position: relative;
}

.page-back-header {
  margin-bottom: 24px;
}

/* ===== 渐入动画 ===== */
@keyframes fadeInUp {
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
}

.animate-fade-in-up {
  animation: fadeInUp 0.5s ease-out forwards;
}

/* ===== 历史记录标签 ===== */
.history-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  background: var(--color-primary-gradient);
  color: var(--color-text);
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  margin-bottom: 16px;
}

/* ===== Hero 综合信息卡 ===== */
.hero-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  margin-bottom: 16px;
}

.hero-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.hero-avatar {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  font-weight: 700;
  flex-shrink: 0;
}

.hero-name {
  font-size: 22px;
  font-weight: 800;
  color: #111827;
  margin: 0 0 4px 0;
}

.hero-meta {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 3px;
}

.hero-time {
  font-size: 12px;
  color: #9ca3af;
}

/* ===== 环形仪表盘 ===== */
.hero-gauge {
  display: flex;
  align-items: center;
  gap: 16px;
}

.gauge-ring {
  position: relative;
  width: 100px;
  height: 100px;
  flex-shrink: 0;
}

.gauge-ring svg {
  transform: rotate(-90deg);
}

.gauge-bg {
  fill: none;
  stroke: #f3f4f6;
  stroke-width: 8;
}

.gauge-fill {
  fill: none;
  stroke-width: 8;
  stroke-linecap: round;
  stroke-dasharray: 282.74;
  transition: stroke-dashoffset 0.8s ease;
}

.gauge-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.gauge-num {
  font-size: 26px;
  font-weight: 800;
  line-height: 1;
  color: #374151;
}

.gauge-label {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
}

.gauge-risk {
  text-align: center;
}

.risk-tag {
  display: inline-block;
  padding: 5px 18px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
}

.risk-1 { background: #dcfce7; color: #16a34a; }
.risk-2 { background: #fef3c7; color: #d97706; }
.risk-3 { background: #fee2e2; color: #dc2626; }

.risk-hint {
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
}

/* ===== 操作按钮 ===== */
.report-actions {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.btn-action {
  padding: 7px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-action:hover {
  border-color: #d1d5db;
  background: #f9fafb;
}

/* ===== 卡片通用 ===== */
.report-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  margin-bottom: 16px;
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 22px 10px;
}

.card-title {
  font-size: 15px;
  font-weight: 800;
  color: #111827;
  margin: 0;
}

.gold-line {
  width: 48px;
  height: 3px;
  background: #fbbf24;
  margin-left: 22px;
  border-radius: 2px;
}

.card-body {
  padding: 18px 22px 22px;
}

/* ===== 风险归因 ===== */
.risk-summary {
  font-size: 14px;
  color: #4b5563;
  line-height: 1.7;
  margin-bottom: 18px;
  padding: 12px 14px;
  background: #fef2f2;
  border-radius: 8px;
  border-left: 3px solid #ef4444;
}

.risk-bars {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.risk-bar-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bar-label {
  width: 68px;
  font-size: 13px;
  color: #6b7280;
  text-align: right;
  flex-shrink: 0;
}

.bar-track {
  flex: 1;
  height: 10px;
  background: #f3f4f6;
  border-radius: 5px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: 5px;
  transition: width 0.6s ease;
}

.bar-val {
  width: 36px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  flex-shrink: 0;
  text-align: right;
}

.risk-reasons {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.risk-reason {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  background: #f9fafb;
  border-radius: 8px;
}

.reason-number {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fee2e2;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  margin-top: 1px;
}

.reason-text {
  flex: 1;
  font-size: 13px;
  color: #4b5563;
  line-height: 1.6;
}

/* ===== 管理建议卡片 ===== */
.suggestion-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.suggestion-card {
  padding: 14px 16px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  transition: all 0.2s;
  cursor: pointer;
}

.suggestion-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
  transform: translateY(-1px);
}

.suggestion-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
}

.suggestion-content {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.6;
}

.suggestion-action {
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
  margin-top: 8px;
}

/* ===== 成功案例 ===== */
.case-block {
  padding: 14px 16px;
  background: #f9fafb;
  border-radius: 10px;
  border-left: 3px solid #d1d5db;
}

.case-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 6px;
}

.case-text {
  font-size: 13px;
  color: #4b5563;
  line-height: 1.7;
}

/* ===== 推荐课程 ===== */
.course-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.course-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  transition: all 0.2s;
  cursor: pointer;
}

.course-row:hover {
  background: #f9fafb;
}

.course-index {
  font-size: 12px;
  font-weight: 700;
  color: #fbbf24;
  width: 24px;
  flex-shrink: 0;
}

.course-info {
  flex: 1;
}

.course-title {
  font-size: 14px;
  font-weight: 500;
  color: #111827;
}

.course-desc {
  font-size: 12px;
  color: #9ca3af;
  margin-top: 1px;
}

.course-arrow {
  font-size: 14px;
  color: #d1d5db;
  transition: color 0.2s;
}

.course-row:hover .course-arrow {
  color: #fbbf24;
}

/* ===== FAQ ===== */
.faq-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.faq-item {
  padding: 12px 14px;
  background: #f9fafb;
  border-radius: 8px;
  border-left: 3px solid #d1d5db;
}

.faq-question {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 6px;
}

.faq-answer {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.7;
}

/* ===== 行为回答详情 ===== */
.behavior-answers {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.behavior-answer-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
}

.behavior-answer-item.answer-1 {
  background: rgba(239, 68, 68, 0.04);
  border-color: rgba(239, 68, 68, 0.15);
}

.behavior-answer-item.answer-2 {
  background: rgba(16, 185, 129, 0.04);
  border-color: rgba(16, 185, 129, 0.15);
}

.behavior-answer-item.answer-3 {
  background: #f9fafb;
}

.answer-number {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #f3f4f6;
  color: #374151;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}

.answer-desc {
  flex: 1;
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
}

.section-count {
  font-size: 12px;
  color: #9ca3af;
  font-weight: 500;
  background: #f3f4f6;
  padding: 2px 10px;
  border-radius: 10px;
}

/* ===== 打印样式 ===== */
@media print {
  .page-back-header,
  .report-actions,
  .history-badge {
    display: none !important;
  }

  .report-page {
    padding: 0;
    max-width: 100%;
  }

  .report-card {
    box-shadow: none;
    border: 1px solid #e5e7eb;
    break-inside: avoid;
  }
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .report-page {
    padding: 16px 12px 32px;
  }

  .hero-card {
    padding: 18px 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }

  .hero-gauge {
    align-self: flex-end;
  }

  .gauge-ring {
    width: 80px;
    height: 80px;
  }

  .gauge-num {
    font-size: 22px;
  }

  .card-header,
  .card-body {
    padding-left: 16px;
    padding-right: 16px;
  }

  .gold-line {
    margin-left: 16px;
  }
}
</style>
